import type Phaser from "phaser";
import { ENTITY_DIRECTION } from "@ninja/contracts";
import type { SpriteAppearance } from "@ninja/asset-catalog";

export type EntityAnimationState =
  "idle" | "walk" | "attack" | "cast" | "death";

export const SERVER_TO_SPRITE_DIRECTION: Readonly<Record<number, number>> = {
  [ENTITY_DIRECTION.north]: 0,
  [ENTITY_DIRECTION.east]: 1,
  [ENTITY_DIRECTION.south]: 2,
  [ENTITY_DIRECTION.west]: 3,
};

export function entityAnimationState(
  serverState: string,
  alive: boolean,
): EntityAnimationState {
  if (!alive || serverState === "death") return "death";
  if (serverState === "move" || serverState === "walk") return "walk";
  if (serverState === "attack") return "attack";
  if (serverState === "cast") return "cast";
  return "idle";
}

export function outfitTextureKey(
  appearanceId: string,
  direction: number,
  frame: number,
): string {
  return `${appearanceId}-d${direction}-f${frame}`;
}

export function outfitWalkAnimationKey(
  appearanceId: string,
  direction: number,
): string {
  return `${appearanceId}-d${direction}-walk`;
}

export class EntityAnimationController {
  constructor(private readonly scene: Phaser.Scene) {}

  registerAppearance(appearance: SpriteAppearance): void {
    if (appearance.sourceFrames.length < 2) return;
    for (const direction of appearance.sourceDirections) {
      const key = outfitWalkAnimationKey(appearance.id, direction);
      if (this.scene.anims.exists(key)) continue;
      this.scene.anims.create({
        key,
        frames: appearance.sourceFrames.map((frame) => ({
          key: outfitTextureKey(appearance.id, direction, frame),
          frame: 0,
        })),
        frameRate: 6,
        repeat: -1,
      });
    }
  }

  apply(
    sprite: Phaser.GameObjects.Sprite,
    appearance: SpriteAppearance,
    serverState: string,
    direction: number,
    alive: boolean,
  ): EntityAnimationState {
    const mappedDirection = SERVER_TO_SPRITE_DIRECTION[direction];
    const safeDirection =
      mappedDirection !== undefined &&
      appearance.sourceDirections.includes(mappedDirection)
        ? mappedDirection
        : (appearance.sourceDirections[0] ?? 0);
    const state = entityAnimationState(serverState, alive);
    if (state === "walk" && appearance.sourceFrames.length > 1) {
      const key = outfitWalkAnimationKey(appearance.id, safeDirection);
      if (sprite.anims.currentAnim?.key !== key || !sprite.anims.isPlaying)
        sprite.play(key);
      return state;
    }

    if (sprite.anims.isPlaying) sprite.anims.stop();
    const firstFrame = appearance.sourceFrames[0] ?? 0;
    const textureKey = outfitTextureKey(
      appearance.id,
      safeDirection,
      firstFrame,
    );
    if (sprite.texture.key !== textureKey || String(sprite.frame.name) !== "0")
      sprite.setTexture(textureKey, 0);
    return state;
  }
}
